import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ApiService } from '../api/api.service';
import { DataStoreService } from '../data-store/data-store.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Amplify } from 'aws-amplify';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '../../../environments/environment';

export interface ChatMessage {
  type: 'user' | 'bot';
  message: string;
  timestamp: Date;
  isUpgradeMessage?: boolean;
}

export interface ChatbotResponse {
  messages: {
    content: string;
    contentType: string;
  }[];
  sessionId: string;
  sessionState: any;
  // Add optional error field for backend error handling
  error?: {
    message: string;
    code?: string;
  };
}

@Injectable({
  providedIn: 'root'
})
export class ChatbotService {
  private chatMessages = new BehaviorSubject<ChatMessage[]>([]);
  public chatMessages$ = this.chatMessages.asObservable();
  private sessionId: string | null = null;
  private sessionState: any = null;
  private currentSquadId: string | null = null;
  
  // Define the API path similar to SessionService
  private chatbotPath = '/chatbot/message';
  
  constructor(
    private apiService: ApiService,
    private dataStoreService: DataStoreService,
    private http: HttpClient,
    private translateService: TranslateService
  ) {
    console.log('ChatbotService constructor called - service instance created');
    this.initializeSession();
  }

  /**
   * Initialize the chatbot session
   * Session ID will be generated by the backend on first message
   */
  private initializeSession(): void {
    // Session ID will be provided by the backend
    this.sessionId = null;
  }

  /**
   * Sets the current squad ID for context
   * @param squadId The squad ID to set
   */
  setSquadId(squadId: string): void {
    this.currentSquadId = squadId;
  }

  /**
   * Sends a message to the chatbot API using EU endpoint
   * @param message The message to send
   * @returns An Observable with the chatbot response
   */
  public sendChatbotMessage(message: string, squadId: string): Observable<ChatbotResponse> {
    // Prepare the request payload with session information
    // Only include sessionId if we have one (backend will create it on first message)
    const payload: any = {
      message,
      squadId: squadId,
      speedUnitEnum: this.dataStoreService.user.coachPreferences.metricUnits.speedUnit,
      distanceUnitEnum: this.dataStoreService.user.coachPreferences.metricUnits.distanceUnit
    };
    
    // Only include sessionId if we have one from a previous response
    if (this.sessionId) {
      payload.sessionId = this.sessionId;
      console.log('Including sessionId in request:', this.sessionId);
    } else {
      console.log('No sessionId available, backend will create one');
    }
    
    console.log('Sending payload:', JSON.stringify(payload, null, 2));
    
    // Use EU endpoint directly instead of the global Amplify configuration
    return this.sendToEUEndpoint(payload);
  }

  /**
   * Sends the request to the EU endpoint directly
   * @param payload The request payload
   * @returns An Observable with the chatbot response
   */
  private sendToEUEndpoint(payload: any): Observable<ChatbotResponse> {
    // Create an observable that handles the async token retrieval
    return new Observable<ChatbotResponse>(observer => {
      this.getEUEndpointRequest(payload).then(response => {
        response.subscribe({
          next: (data) => observer.next(data),
          error: (error) => observer.error(error),
          complete: () => observer.complete()
        });
      }).catch(error => {
        console.error('Error setting up EU endpoint request:', error);
        // Fallback to regular API service if token retrieval fails
        this.apiService.post(this.chatbotPath, payload).subscribe({
          next: (data) => observer.next(data),
          error: (error) => observer.error(error),
          complete: () => observer.complete()
        });
      });
    });
  }

  /**
   * Helper method to get the EU endpoint request with proper authentication
   * @param payload The request payload
   * @returns A Promise that resolves to an Observable
   */
  private async getEUEndpointRequest(payload: any): Promise<Observable<ChatbotResponse>> {
    // Get the authorization token
    const user = await Amplify.Auth.currentAuthenticatedUser();
    const token = user.signInUserSession.idToken.jwtToken;
    
    // Set up headers
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    });
    
    // Use EU endpoint directly
    const euEndpoint = environment.awsEUServer + this.chatbotPath;
    
    // Make the HTTP request directly to EU endpoint
    return this.http.post<ChatbotResponse>(euEndpoint, payload, { headers });
  }

  /**
   * Sends a message to the Lex bot and handles the response
   * @param message The message to send to the bot
   * @returns A promise that resolves when the message is sent and the response is received
   */
  async sendMessage(message: string, squadId: string): Promise<void> {
    // Add the user message to the chat
    this.addMessage({
      type: 'user',
      message,
      timestamp: new Date()
    });

    try {
      
      // Log current session ID before sending
      console.log('Current session ID before sending message:', this.sessionId);
      
      // Send the message using the API service
      const response = await this.sendChatbotMessage(message, squadId).toPromise();
      
      // Log the full response to debug
      console.log('Full response from backend:', JSON.stringify(response, null, 2));
      
      // IMPORTANT: Store the session ID from the backend response FIRST, before any error checks
      // This ensures we always save the sessionId even if there are other issues
      if (response && response.sessionId) {
        const previousSessionId = this.sessionId;
        this.sessionId = response.sessionId;
        console.log('Session ID updated - Previous:', previousSessionId, 'New:', this.sessionId);
      } else {
        console.warn('No sessionId in response. Response keys:', response ? Object.keys(response) : 'null');
      }
      
      // Check if the backend returned an error
      if (response && response.error) {
        console.error('Backend returned an error:', response.error);
        this.addMessage({
          type: 'bot',
          message: response.error.message || this.translateService.instant('CHATBOT.ERROR_MESSAGE') || 'Sorry, I encountered an error. Please try again later.',
          timestamp: new Date()
        });
        return;
      }
      
      // Store the session state from the response for the next message
      if (response && response.sessionState) {
        this.sessionState = response.sessionState;
      }
      
      // Extract the message from the response
      let botMessage = '';
      
      if (response && response.messages && response.messages.length > 0) {
        // Process the response format
        botMessage = response.messages.map(msg => msg.content).join('\n');

        // Define all possible intent types
        const intentTypes = [
          'FAQ_QUERY',
          'OTHER_QUERY',
          'GIBBERISH',
          'GREETINGS',
          'AFFIRMATION',
          'CHITCHAT',
          'MIS'
        ];

        // Check if message contains answer tag and any of the intent types
        const hasAnswerTag = botMessage.includes('<answer>');
        const hasIntentTag = intentTypes.some(intent => 
          botMessage.includes(`<intent>${intent}</intent>`)
        );

        if (hasAnswerTag && hasIntentTag) {
          // Remove all the XML-like tags and any extra newlines
          botMessage = botMessage
            .replace('<answer>', '')
            .replace('</answer>', '')
            // Remove all intent tags dynamically
            .replace(new RegExp(`<intent>(${intentTypes.join('|')})</intent>`, 'g'), '')
            .replace('<text>', '')
            .replace('</text>', '')
            .trim();

          // Clean up any extra newlines at the start
          while (botMessage.startsWith('\n')) {
            botMessage = botMessage.substring(1);
          }

          // Debug: Log the message before processing
          console.log('Message before newline processing:', JSON.stringify(botMessage));

          // Convert \n to actual line breaks and format properly
          botMessage = botMessage
            // Convert literal \n to actual newlines first
            .replace(/\\n/g, '\n')
            // Convert any remaining \n sequences to actual newlines
            .replace(/\n/g, '\n')
            // Clean up multiple consecutive newlines (more than 2)
            .replace(/\n{3,}/g, '\n\n')
            // Ensure bullet points have proper spacing
            .replace(/\n-/g, '\n\n-')
            // Clean up any trailing whitespace
            .trim();

          // Debug: Log the message after newline processing but before br conversion
          console.log('Message after newline processing:', JSON.stringify(botMessage));

          // Convert ALL types of newlines to HTML br tags for display
          botMessage = botMessage
            .replace(/\r\n/g, '<br>')  // Windows line endings
            .replace(/\r/g, '<br>')    // Mac line endings
            .replace(/\n/g, '<br>');   // Unix line endings

          // Convert bullet points to properly formatted HTML list items
          botMessage = this.formatBulletPoints(botMessage);

          // Debug: Log the final message
          console.log('Final message with br tags:', JSON.stringify(botMessage));

          // Find the last complete sentence (ending with ., !, or ?)
          const lastPunctuationIndex = Math.max(
            botMessage.lastIndexOf('.'),
            botMessage.lastIndexOf('!'),
            botMessage.lastIndexOf('?')
          );
          
          if (lastPunctuationIndex > 0) {
            // Cut at the last punctuation mark (including the punctuation)
            botMessage = botMessage.substring(0, lastPunctuationIndex + 1);
            
            // Add friendly continuation message
          }
        }
      } else {
        // Default error message if no response is received
        botMessage = this.translateService.instant('CHATBOT.ERROR_NO_RESPONSE') || 'Sorry, I did not understand that.';
      }
      
      
      // Add the bot response to the chat
      this.addMessage({
        type: 'bot',
        message: botMessage,
        timestamp: new Date()
      });
    } catch (error) {
      // Simplified error handling since we're using the backend
      console.error('Error communicating with chatbot API:', error);
      
      // Add a generic error message to the chat
      this.addMessage({
        type: 'bot',
        message: this.translateService.instant('CHATBOT.ERROR_MESSAGE') || 'Sorry, I encountered an error. Please try again later.',
        timestamp: new Date()
      });
    }
  }

  /**
   * Adds a message to the chat history
   * @param message The message to add
   */
  addMessage(message: ChatMessage): void {
    const currentMessages = this.chatMessages.getValue();
    this.chatMessages.next([...currentMessages, message]);
  }

  /**
   * Clears the chat history and resets the conversation
   */
  clearChat(): void {
    this.chatMessages.next([]);
    this.resetConversation();
  }

  /**
   * Resets the conversation by clearing session ID and session state
   * A new session ID will be generated by the backend on the next message
   */
  private resetConversation(): void {
    this.sessionId = null;
    this.sessionState = null;
    console.log('Conversation reset - new session ID will be generated by backend on next message');
  }

  /**
   * Gets the current session ID
   * @returns The current session ID
   */
  getSessionId(): string {
    return this.sessionId || '';
  }

  /**
   * Formats bullet points into properly styled HTML list items
   * @param message The message containing bullet points
   * @returns Formatted message with HTML list styling
   */
  private formatBulletPoints(message: string): string {
    // Split the message into lines
    const lines = message.split('<br>');
    let formattedLines: string[] = [];
    let inList = false;
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      
      // Check if this line is a bullet point (starts with -)
      if (line.startsWith('-')) {
        // If we're not already in a list, start one
        if (!inList) {
          formattedLines.push('<ul style="margin: 8px 0; padding-left: 20px;">');
          inList = true;
        }
        
        // Remove the dash and create a list item
        const bulletContent = line.substring(1).trim();
        formattedLines.push(`<li style="margin-bottom: 4px; line-height: 1.4;">${bulletContent}</li>`);
      } else {
        // If we were in a list and this line is not a bullet point, close the list
        if (inList) {
          formattedLines.push('</ul>');
          inList = false;
        }
        
        // Add the regular line (but only if it's not empty)
        if (line.length > 0) {
          formattedLines.push(line);
        }
      }
    }
    
    // Close any open list at the end
    if (inList) {
      formattedLines.push('</ul>');
    }
    
    // Join the lines back together with <br> tags, but don't add <br> before/after <ul>/<li> tags
    let result = '';
    for (let i = 0; i < formattedLines.length; i++) {
      const currentLine = formattedLines[i];
      const nextLine = formattedLines[i + 1];
      
      result += currentLine;
      
      // Only add <br> if:
      // - This is not the last line
      // - Current line is not a list tag (<ul>, </ul>, <li>)
      // - Next line is not a list tag
      if (i < formattedLines.length - 1 &&
          !currentLine.includes('<ul>') && 
          !currentLine.includes('</ul>') && 
          !currentLine.includes('<li>') &&
          nextLine &&
          !nextLine.includes('<ul>') && 
          !nextLine.includes('</ul>') && 
          !nextLine.includes('<li>')) {
        result += '<br>';
      }
    }
    
    return result;
  }
} 